/**
 * Several clearings, each with its name (Daniel, 2026-10-22: a supporter may have as many as they
 * like, and he already wants several to play with). The player chooses among them in a row under
 * the top bar, as his sketch of that day draws it: `docs/design/clearing.md`, More clearings. On
 * the web every player has them, as a trial, while only Daniel and a friend play (decision 0016).
 * Saved in this browser beside the finds, which every clearing shares.
 */

import { type ClearingState, defaultClearing, loadClearing, parseClearing } from "./clearing.ts";
import { getJson, setJson } from "./storage.ts";

/**
 * The names new clearings take, in this order: Nordic place names, the same in every language as
 * the names of the people in the houses are (decision 0023). The first clearing is the first.
 * When every name is taken they come round again with a number.
 */
export const PLACE_NAMES: readonly string[] = [
  "Granbacken",
  "Sjöviken",
  "Björkudden",
  "Tallmon",
  "Mossmyren",
  "Stenåsen",
  "Ljungheden",
  "Rönnängen",
  "Furulid",
  "Dimmudden",
  "Trollberget",
  "Tjärnliden",
  "Vassviken",
  "Hallonbacken",
  "Lingonmon",
  "Älgmyren",
  "Uvberget",
  "Kvarnbäcken",
  "Fäbodvallen",
  "Ekbacken",
  "Ormnäset",
  "Kolbotten",
  "Svansjön",
  "Hägerholmen",
];

/** The longest name a clearing takes, in characters. */
export const NAME_MAX = 24;

const KEY = "clearings";

export interface NamedClearing {
  /** Stays with the clearing for as long as it is kept, whatever its name or place in the row. */
  readonly id: number;
  readonly name: string;
  readonly state: ClearingState;
}

export interface Clearings {
  /** The id of the clearing that shows. */
  readonly chosen: number;
  /** In the order they were made, the newest last, by the row's plus. */
  readonly list: readonly NamedClearing[];
}

/** A name as a clearing keeps it: spaces run together and trimmed, at most NAME_MAX characters. */
export function cleanName(raw: string): string {
  const name = raw.replace(/\s+/g, " ").trim();
  return Array.from(name).slice(0, NAME_MAX).join("").trim();
}

/** The first place name no clearing has, then the names again with 2, 3 and so on. */
export function nextName(taken: readonly string[]): string {
  const used = new Set(taken);
  for (let round = 1; ; round += 1) {
    for (const name of PLACE_NAMES) {
      const candidate = round === 1 ? name : `${name} ${round}`;
      if (!used.has(candidate)) return candidate;
    }
  }
}

/** One clearing, the player's first, under the first place name. */
export function firstClearings(state: ClearingState): Clearings {
  return { chosen: 1, list: [{ id: 1, name: PLACE_NAMES[0] as string, state }] };
}

/** The clearing that shows. */
export function chosenOf(clearings: Clearings): NamedClearing {
  return (
    clearings.list.find((c) => c.id === clearings.chosen) ?? (clearings.list[0] as NamedClearing)
  );
}

/** A new clearing at the end of the row, as the first one started, under the next free name. */
export function addClearing(clearings: Clearings): Clearings {
  const id = Math.max(0, ...clearings.list.map((c) => c.id)) + 1;
  const name = nextName(clearings.list.map((c) => c.name));
  return { chosen: id, list: [...clearings.list, { id, name, state: defaultClearing() }] };
}

export function chooseClearing(clearings: Clearings, id: number): Clearings {
  if (id === clearings.chosen || !clearings.list.some((c) => c.id === id)) return clearings;
  return { ...clearings, chosen: id };
}

/** A new name; an empty one changes nothing. */
export function renameClearing(clearings: Clearings, id: number, raw: string): Clearings {
  const name = cleanName(raw);
  const named = clearings.list.find((c) => c.id === id);
  if (!name || !named || named.name === name) return clearings;
  return { ...clearings, list: clearings.list.map((c) => (c.id === id ? { ...c, name } : c)) };
}

/**
 * Deletes a clearing and what was set out in it; the finds stay. The last clearing stays. When the
 * one showing goes, the one before it shows, or the one after when it was the first.
 */
export function deleteClearing(clearings: Clearings, id: number): Clearings {
  const i = clearings.list.findIndex((c) => c.id === id);
  if (i < 0 || clearings.list.length <= 1) return clearings;
  const list = clearings.list.filter((c) => c.id !== id);
  const chosen =
    clearings.chosen === id ? (list[Math.max(0, i - 1)] as NamedClearing).id : clearings.chosen;
  return { chosen, list };
}

/** The clearing after an edit, kept under its id. */
export function updateClearing(clearings: Clearings, id: number, state: ClearingState): Clearings {
  return {
    ...clearings,
    list: clearings.list.map((c) => (c.id === id && c.state !== state ? { ...c, state } : c)),
  };
}

/**
 * Saved clearings, checked as one clearing is (`parseClearing`); an entry that does not read is
 * dropped. Null when none reads.
 */
export function parseClearings(raw: unknown, names: ReadonlySet<string>): Clearings | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as { v?: unknown; chosen?: unknown; list?: unknown };
  if (r.v !== 1 || !Array.isArray(r.list)) return null;
  const list: NamedClearing[] = [];
  for (const entry of r.list as unknown[]) {
    if (typeof entry !== "object" || entry === null) continue;
    const { id, name, clearing } = entry as { id?: unknown; name?: unknown; clearing?: unknown };
    if (typeof id !== "number" || !Number.isInteger(id) || id < 1) continue;
    if (list.some((c) => c.id === id)) continue;
    const state = parseClearing(clearing, names);
    if (!state) continue;
    const clean = typeof name === "string" ? cleanName(name) : "";
    list.push({ id, name: clean || nextName(list.map((c) => c.name)), state });
  }
  const first = list[0];
  if (!first) return null;
  const chosen = list.some((c) => c.id === r.chosen) ? (r.chosen as number) : first.id;
  return { chosen, list };
}

/**
 * The saved clearings; before there were several, the one clearing saved under its old key
 * becomes the first, and that key is left as it was.
 */
export function loadClearings(names: ReadonlySet<string>): Clearings {
  return parseClearings(getJson<unknown>(KEY), names) ?? firstClearings(loadClearing(names));
}

export function saveClearings(clearings: Clearings): void {
  setJson(KEY, {
    v: 1,
    chosen: clearings.chosen,
    list: clearings.list.map((c) => ({ id: c.id, name: c.name, clearing: c.state })),
  });
}
